import { Component, useEffect } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { I18nProvider, useI18n } from "@/context/I18nContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import Home from "@/pages/Home";
import Reservas from "@/pages/Reservas";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import Seo from "@/components/Seo";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
          else el.scrollIntoView({ behavior: "smooth" });
        }, 350);
        return;
      }
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-charcoal px-5 text-center text-cream">
      <Seo title={t.seo.notFound.title} description={t.seo.notFound.description} />
      <p className="font-corm text-7xl italic text-terra">404</p>
      <h1 className="mt-4 font-serif text-3xl">{t.common.notFoundTitle}</h1>
      <p className="mt-3 max-w-sm text-sm text-cream/60">{t.common.notFoundText}</p>
      <Link
        to="/"
        data-testid="notfound-home-link"
        className="mt-8 rounded-full bg-terra px-8 py-3.5 text-sm font-medium text-cream transition-colors hover:bg-terra-dark"
      >
        {t.common.backHome}
      </Link>
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }

  static getDerivedStateFromError() {
    return { error: true };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-charcoal px-5 text-center text-cream">
          <p className="font-serif text-3xl">Wild Spoon</p>
          <p className="mt-3 text-sm text-cream/60">Algo no fue bien. Recarga la página para continuar.</p>
          <a href="/" className="mt-8 rounded-full bg-terra px-8 py-3.5 text-sm font-medium text-cream">
            Volver al inicio
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  return (
    <>
      {!isAdmin && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/reservas" element={<Reservas />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/aviso-legal" element={<Legal kind="aviso" />} />
        <Route path="/privacidad" element={<Legal kind="privacidad" />} />
        <Route path="/cookies" element={<Legal kind="cookies" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdmin && <Footer />}
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <I18nProvider>
        <BrowserRouter>
          <ScrollManager />
          <CookieConsent />
          <Shell />
        </BrowserRouter>
      </I18nProvider>
    </ErrorBoundary>
  );
}
